import * as Sentry from '@sentry/react';

import { IFieldState } from '../../store/applicationform/applicationFormTypes';
import { KycAnswer } from '../../types/KycTypes';

/** `KYC_<groupId>_<questionId>`, which is how the field names are built when the questions render. */
const FIELD_NAME_PART_COUNT = 3;

/**
 * Mirrors the emptiness rule in useValidation, so an answer the customer was allowed to skip past is never posted.
 * The looser `!== ''` this replaces let `undefined` through, which then reached the backend as the string "undefined".
 */
const hasAnswer = (field: IFieldState): boolean =>
    Array.isArray(field.value) ? field.value.length > 0 : field.value !== undefined && field.value !== '';

/**
 * Turns the KYC field states into the answers save_survey_answers expects.
 *
 * The parse assumes a group id with no underscore in it. That holds for every template seen so far ("new-account",
 * "ECONOMY", "CITIZENSHIP", "PEP") and nothing upstream enforces it, so a name that does not parse is reported rather
 * than dropped in silence -- losing every answer in a group behind an HTTP 200 is not something to discover from a
 * customer. Note this endpoint is the safe one either way: it takes category and question already separated. The
 * submit path is where an underscored group id would actually corrupt answers, and that split happens server-side in
 * consumerloan-application-service.
 */
export const convertToAnswers = (kycAnswerFieldStates: IFieldState[]): KycAnswer[] =>
    kycAnswerFieldStates
        .filter(hasAnswer)
        .filter((field) => {
            if (field.fieldName.split('_').length === FIELD_NAME_PART_COUNT) {
                return true;
            }

            Sentry.captureException(new Error('Skipping a KYC answer whose field name does not parse'), {
                tags: { source: 'kyc_answers' },
                extra: { fieldName: field.fieldName },
            });
            return false;
        })
        .flatMap((fieldState) => {
            const [, category, question] = fieldState.fieldName.split('_');

            if (Array.isArray(fieldState.value)) {
                return fieldState.value.map((item) => ({ category, question, answer: item }));
            }

            return [{ category, question, answer: String(fieldState.value) }];
        });
